import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Breadcrumbs,
  Box,
  Button,
  Card,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowBackRounded } from "@mui/icons-material";
import { doc, getDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import MainLayout from "../../layouts/main_layout/main_layout";
import { useFeedback } from "../../providers/feedback/feedback";
import { firestore } from "../..";
import { deaneryList, parishListMap, ParishOption } from "../../utils/deanery_parishes";
import {
  YOUCAT_MONTHLY_FORM_CONFIGS_COLLECTION,
  YoucatMonthlyQuestion,
} from "../admin_portal/youcat/youcat_monthly_form_tab";

const YOUCAT_SUBMIT_ENDPOINT =
  "https://submityoucatmonthlyupdate-f5v5da2yxa-uc.a.run.app";
const MAX_ESTIMATED_REQUEST_BYTES = 5 * 1024 * 1024;
const MAX_IMAGE_COUNT = 3;
const MAX_ORIGINAL_IMAGE_BYTES = 500 * 1024;
const TARGET_COMPRESSED_IMAGE_BYTES = 200 * 1024;
const MAX_COMPRESSION_DIMENSION = 1600;

type BaseFormValues = {
  dateOfConduction: string;
  deanery: string;
  parish: string;
  youcatInchargeName: string;
  email: string;
  contact: string;
  organization: "ICYM" | "YCS" | "";
  youthsParticipatedCount: string;
};

const EMPTY_FORM: BaseFormValues = {
  dateOfConduction: new Date().toISOString().slice(0, 10),
  deanery: "",
  parish: "",
  youcatInchargeName: "",
  email: "",
  contact: "",
  organization: "",
  youthsParticipatedCount: "",
};

function getCurrentIstMonthKey() {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
  });
  const parts = formatter.formatToParts(new Date());
  const year = parts.find((part) => part.type === "year")?.value || "";
  const month = parts.find((part) => part.type === "month")?.value || "";
  return `${year}-${month}`;
}

function fileToDataUrl(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Failed to read image"));
    reader.readAsDataURL(file);
  });
}

function formatFileSize(bytes: number) {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }

  return `${Math.round(bytes / 1024)} KB`;
}

function estimateBase64PayloadSize(files: File[]) {
  return files.reduce((total, file) => total + Math.ceil((file.size * 4) / 3), 0);
}

function getFileExtension(name: string) {
  const parts = name.split(".");
  return parts.length > 1 ? parts.pop() || "" : "";
}

function getCompressedFileName(name: string) {
  const extension = getFileExtension(name);
  const baseName = extension ? name.slice(0, -(extension.length + 1)) : name;
  return `${baseName || "image"}-compressed.jpg`;
}

function loadImage(file: File) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const imageUrl = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(imageUrl);
      resolve(image);
    };

    image.onerror = () => {
      URL.revokeObjectURL(imageUrl);
      reject(new Error(`Failed to load image: ${file.name}`));
    };

    image.src = imageUrl;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, quality: number) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Failed to compress image"));
          return;
        }

        resolve(blob);
      },
      "image/jpeg",
      quality,
    );
  });
}

async function compressImage(file: File) {
  const image = await loadImage(file);
  const scale = Math.min(
    1,
    MAX_COMPRESSION_DIMENSION / Math.max(image.naturalWidth, image.naturalHeight),
  );
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));

  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Failed to initialize image compression");
  }

  context.drawImage(image, 0, 0, canvas.width, canvas.height);

  let quality = 0.82;
  let blob = await canvasToBlob(canvas, quality);

  while (blob.size > TARGET_COMPRESSED_IMAGE_BYTES && quality > 0.4) {
    quality -= 0.08;
    blob = await canvasToBlob(canvas, quality);
  }

  if (blob.size > TARGET_COMPRESSED_IMAGE_BYTES) {
    throw new Error(
      `${file.name} could not be compressed under ${formatFileSize(TARGET_COMPRESSED_IMAGE_BYTES)}`,
    );
  }

  return new File([blob], getCompressedFileName(file.name), {
    type: "image/jpeg",
    lastModified: Date.now(),
  });
}

async function parseJsonSafe(response: Response) {
  const responseText = await response.text();

  if (!responseText.trim()) {
    return null;
  }

  try {
    return JSON.parse(responseText);
  } catch {
    return {
      error: responseText,
    };
  }
}

export default function YoucatMonthlyUpdateFormPage() {
  const navigate = useNavigate();
  const { showSnackbar } = useFeedback();
  const [formValues, setFormValues] = useState<BaseFormValues>(EMPTY_FORM);
  const [questions, setQuestions] = useState<YoucatMonthlyQuestion[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [parishOptions, setParishOptions] = useState<ParishOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const currentMonthKey = useMemo(() => getCurrentIstMonthKey(), []);

  useEffect(() => {
    let isMounted = true;

    async function loadCurrentForm() {
      try {
        const snapshot = await getDoc(
          doc(firestore, YOUCAT_MONTHLY_FORM_CONFIGS_COLLECTION, currentMonthKey),
        );

        if (!isMounted) {
          return;
        }

        if (snapshot.exists()) {
          const data = snapshot.data() as { questions?: YoucatMonthlyQuestion[] };
          setQuestions(data.questions || []);
          return;
        }

        setQuestions([]);
      } catch (error) {
        console.error("Failed to load current YouCat monthly form", error);
        showSnackbar("Failed to load the current monthly form");
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadCurrentForm();

    return () => {
      isMounted = false;
    };
  }, [currentMonthKey, showSnackbar]);

  function validateEmail(email: string) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  function validatePhone(phone: string) {
    return /^\d{10}$/.test(phone.trim());
  }

  async function handleImageSelection(event: React.ChangeEvent<HTMLInputElement>) {
    const incomingFiles = event.target.files ? Array.from(event.target.files) : [];

    if (!incomingFiles.length) {
      setSelectedImages([]);
      return;
    }

    if (incomingFiles.length > MAX_IMAGE_COUNT) {
      showSnackbar(`Please upload not more than ${MAX_IMAGE_COUNT} images`);
      event.target.value = "";
      return;
    }

    const oversizedFile = incomingFiles.find(
      (file) => file.size > MAX_ORIGINAL_IMAGE_BYTES,
    );

    if (oversizedFile) {
      showSnackbar(
        `${oversizedFile.name} is too large. Please choose images up to ${formatFileSize(
          MAX_ORIGINAL_IMAGE_BYTES,
        )}.`,
      );
      event.target.value = "";
      return;
    }

    try {
      setIsSaving(true);
      const compressedFiles = await Promise.all(
        incomingFiles.map((file) => compressImage(file)),
      );
      setSelectedImages(compressedFiles);
    } catch (error: any) {
      console.error("Failed to compress selected YouCat images", error);
      showSnackbar(error?.message || "Failed to prepare selected images");
      event.target.value = "";
      setSelectedImages([]);
    } finally {
      setIsSaving(false);
    }
  }

  async function handleSubmit() {
    if (
      !formValues.dateOfConduction ||
      !formValues.deanery.trim() ||
      !formValues.parish.trim() ||
      !formValues.youcatInchargeName.trim() ||
      !formValues.email.trim() ||
      !formValues.contact.trim() ||
      !formValues.organization ||
      !formValues.youthsParticipatedCount.trim()
    ) {
      showSnackbar("Please fill all required base fields");
      return;
    }

    if (!validateEmail(formValues.email.trim())) {
      showSnackbar("Please enter a valid email address");
      return;
    }

    if (!validatePhone(formValues.contact)) {
      showSnackbar("Please enter a valid 10 digit contact number");
      return;
    }

    if (!Number.isInteger(Number(formValues.youthsParticipatedCount)) || Number(formValues.youthsParticipatedCount) < 0) {
      showSnackbar("Please enter a valid youths participated count");
      return;
    }

    if (selectedImages.length < 1 || selectedImages.length > MAX_IMAGE_COUNT) {
      showSnackbar(`Please upload at least 1 image and not more than ${MAX_IMAGE_COUNT}`);
      return;
    }

    if (estimateBase64PayloadSize(selectedImages) > MAX_ESTIMATED_REQUEST_BYTES) {
      showSnackbar(
        "Selected images are too large to submit together. Please reduce the number of images or choose smaller files.",
      );
      return;
    }

    const missingQuestion = questions.find(
      (question) => question.required && !answers[question.id]?.trim(),
    );

    if (missingQuestion) {
      showSnackbar(`Please answer: ${missingQuestion.question}`);
      return;
    }

    try {
      setIsSaving(true);

      const preparedImages = await Promise.all(
        selectedImages.map(async (file) => {
          const dataUrl = await fileToDataUrl(file);
          const base64Data = dataUrl.split(",")[1] || "";

          return {
            name: file.name,
            type: file.type,
            data: base64Data,
          };
        }),
      );

      const response = await fetch(YOUCAT_SUBMIT_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...formValues,
          deanery: formValues.deanery.trim(),
          parish: formValues.parish.trim(),
          youcatInchargeName: formValues.youcatInchargeName.trim(),
          email: formValues.email.trim(),
          contact: formValues.contact.trim(),
          youthsParticipatedCount: Number(formValues.youthsParticipatedCount),
          answers: questions.reduce<Record<string, string>>((acc, question) => {
            acc[question.question] = answers[question.id] || "";
            return acc;
          }, {}),
          images: preparedImages,
        }),
      });

      const result = await parseJsonSafe(response);

      if (!response.ok) {
        throw new Error(result?.error || "Submission failed");
      }

      showSnackbar("Monthly update submitted successfully");
      navigate("/youcat");
    } catch (error: any) {
      console.error("Failed to submit YouCat monthly update", error);
      showSnackbar(error?.message || "Failed to submit monthly update");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <MainLayout>
      <Box my={{ xs: 2, md: 4 }}>
        <Box
          maxWidth={980}
          mx="auto"
          mb={2}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
          flexWrap="wrap"
        >
          <Breadcrumbs aria-label="breadcrumb">
            <Button
              startIcon={<ArrowBackRounded />}
              onClick={() => navigate("/youcat")}
              sx={{ px: 0, minWidth: "auto" }}
            >
              Back to YouCat
            </Button>
          </Breadcrumbs>
        </Box>
        <Card
          sx={{
            maxWidth: 980,
            mx: "auto",
            p: { xs: 2.5, md: 4 },
            borderRadius: 4,
            background:
              "linear-gradient(180deg, rgba(255,251,235,1) 0%, rgba(255,255,255,1) 100%)",
            border: "1px solid rgba(217,119,6,0.14)",
            boxShadow: "0 24px 48px rgba(180, 120, 20, 0.12)",
          }}
        >
          <Typography variant="overline" sx={{ color: "#a16207", fontWeight: 800 }}>
            Monthly Updates Form
          </Typography>
          <Typography variant="h4" fontWeight={900} mt={1} mb={1}>
            YouCat Monthly Update
          </Typography>
          <Typography color="#7c651d" mb={4}>
            This form is automatically active for the current month based on
            Indian Standard Time.
          </Typography>

          {isLoading ? (
            <Typography>Loading current monthly form...</Typography>
          ) : (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Date of Conduction"
                  value={formValues.dateOfConduction}
                  onChange={(event) =>
                    setFormValues((prev) => ({
                      ...prev,
                      dateOfConduction: event.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  fullWidth
                  options={deaneryList}
                  value={deaneryList.find((item) => item.name === formValues.deanery) || null}
                  onChange={(_event, value) => {
                    setFormValues((prev) => ({
                      ...prev,
                      deanery: value?.name || "",
                      parish: "",
                    }));
                    setParishOptions(value ? parishListMap[value.name] || [] : []);
                  }}
                  getOptionLabel={(option) => option.name || ""}
                  renderInput={(params) => (
                    <TextField {...params} required label="Deanery" />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  fullWidth
                  options={parishOptions}
                  value={parishOptions.find((item) => item.value === formValues.parish) || null}
                  onChange={(_event, value) =>
                    setFormValues((prev) => ({
                      ...prev,
                      parish: value?.value || "",
                    }))
                  }
                  getOptionLabel={(option) => option.value || ""}
                  renderInput={(params) => (
                    <TextField {...params} required label="Parish" />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Name of YouCat In-charge"
                  value={formValues.youcatInchargeName}
                  onChange={(event) =>
                    setFormValues((prev) => ({
                      ...prev,
                      youcatInchargeName: event.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  required
                  type="email"
                  label="Email"
                  value={formValues.email}
                  onChange={(event) =>
                    setFormValues((prev) => ({
                      ...prev,
                      email: event.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  required
                  label="Contact"
                  value={formValues.contact}
                  onChange={(event) =>
                    setFormValues((prev) => ({
                      ...prev,
                      contact: event.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  required
                  label="Unit ICYM or YCS"
                  value={formValues.organization}
                  onChange={(event) =>
                    setFormValues((prev) => ({
                      ...prev,
                      organization: event.target.value as "ICYM" | "YCS",
                    }))
                  }
                >
                  <MenuItem value="ICYM">ICYM</MenuItem>
                  <MenuItem value="YCS">YCS</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  required
                  label="Youths Participated Count"
                  value={formValues.youthsParticipatedCount}
                  onChange={(event) =>
                    setFormValues((prev) => ({
                      ...prev,
                      youthsParticipatedCount: event.target.value,
                    }))
                  }
                />
              </Grid>

              {questions.map((question) => (
                <Grid item xs={12} key={question.id}>
                  {question.type === "mcq" ? (
                    <TextField
                      select
                      fullWidth
                      label={question.question}
                      required={question.required}
                      value={answers[question.id] || ""}
                      onChange={(event) =>
                        setAnswers((prev) => ({
                          ...prev,
                          [question.id]: event.target.value,
                        }))
                      }
                    >
                      {question.options.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  ) : (
                    <TextField
                      fullWidth
                      multiline={question.type === "long_answer"}
                      minRows={question.type === "long_answer" ? 4 : 1}
                      label={question.question}
                      required={question.required}
                      value={answers[question.id] || ""}
                      onChange={(event) =>
                        setAnswers((prev) => ({
                          ...prev,
                          [question.id]: event.target.value,
                        }))
                      }
                    />
                  )}
                </Grid>
              ))}

              <Grid item xs={12}>
                <Typography fontWeight={700} mb={1}>
                  Upload Images
                </Typography>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageSelection}
                />
                <Typography color="text.secondary" mt={1}>
                  Upload 1 to 3 images. Each image must be 500 KB or smaller and
                  will be compressed below 200 KB before upload.
                </Typography>
                {selectedImages.length ? (
                  <Stack spacing={0.5} mt={1.5}>
                    {selectedImages.map((file) => (
                      <Typography
                        key={`${file.name}-${file.lastModified}`}
                        variant="body2"
                        color="text.secondary"
                      >
                        {file.name} ({formatFileSize(file.size)})
                      </Typography>
                    ))}
                    <Typography variant="body2" color="text.secondary">
                      Estimated upload size: {formatFileSize(estimateBase64PayloadSize(selectedImages))}
                    </Typography>
                  </Stack>
                ) : null}
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" gap={2} flexWrap="wrap">
                  <Button variant="outlined" onClick={() => navigate("/youcat")}>
                    Cancel
                  </Button>
                  <Button variant="contained" onClick={handleSubmit} disabled={isSaving}>
                    {isSaving ? "Submitting..." : "Submit Monthly Update"}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          )}
        </Card>
      </Box>
    </MainLayout>
  );
}
